// Export the tour steps for Appearance Settings form fields
export const appearanceSettingsGuideSteps = [
  {
    element: "#AS-theme-selection",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Theme Selection</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Choose from predefined theme styles that control the overall layout and visual structure of your brand details page.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Each theme offers a unique layout design</li>
              <li>• Affects how content is organized and displayed</li>
              <li>• Preview updates instantly when you change themes</li>
              <li>• Try different themes to find the best fit for your brand</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#AS-social-media-icon-size",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Social Media Icon Size</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Customize the size of social media icons that appear on your brand details page.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Small: Subtle and minimal appearance</li>
              <li>• Medium: Balanced visibility (recommended)</li>
              <li>• Large: Bold and prominent display</li>
              <li>• Icons link to your social media profiles</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#AS-primary-color",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Primary Color</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Set the main color for your brand details page. This color will be used for buttons, links, and key interactive elements.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Use your brand's primary color for consistency</li>
              <li>• Click the color box to open the color picker</li>
              <li>• Or type a hex code directly (e.g., #7C3AED)</li>
              <li>• Preview updates instantly to show your changes</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#AS-secondary-color",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Secondary Color</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Choose a complementary color for backgrounds, borders, and secondary UI elements on your brand page.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Should complement your primary color</li>
              <li>• Often a lighter shade of your brand color</li>
              <li>• Used for subtle accents and backgrounds</li>
              <li>• Preview shows how colors work together</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#AS-showcase-images",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Brand Showcase Images</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Upload compelling images that showcase your brand, products, or company culture to create an engaging brand page.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Upload up to 5 high-quality images</li>
              <li>• Click "Upload Image" to add new photos</li>
              <li>• Drag and drop to reorder images</li>
              <li>• Use images that represent your brand story</li>
              <li>• Remove images by clicking the X button</li>
            </ul>
          </div>
        </div>`,
      side: "left" as const,
      align: "start" as const,
    },
  },
  {
    element: "#AS-product-showcase",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Product Showcase</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Select up to 4 featured products to highlight on your brand details page. These will be the first products potential affiliates see.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Choose your best-selling or flagship products</li>
              <li>• Select up to 4 products to showcase</li>
              <li>• Click on products to select/deselect them</li>
              <li>• Products must be added to your store first</li>
              <li>• These products will be prominently displayed</li>
            </ul>
          </div>
        </div>`,
      side: "left" as const,
      align: "start" as const,
    },
  },
  {
    element: "#AS-preview-section",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Live Preview</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">See your brand details page exactly as affiliates and customers will see it. All changes update in real-time.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Preview updates instantly as you make changes</li>
              <li>• Changed elements are highlighted with animations</li>
              <li>• Scroll through to see the full page layout</li>
              <li>• Test different settings before saving</li>
            </ul>
          </div>
        </div>`,
      side: "left" as const,
      align: "start" as const,
    },
  },
  {
    element: "#AS-save-button",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Save Your Changes</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">When you're happy with your appearance settings, click "Save Changes" to apply them to your live brand page.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Save button appears when you make changes</li>
              <li>• "Reset" button discards unsaved changes</li>
              <li>• Changes take effect immediately after saving</li>
              <li>• You can edit your settings anytime</li>
            </ul>
          </div>
        </div>`,
      side: "bottom" as const,
      align: "end" as const,
    },
  },
];
